import { useCallback, useEffect, useMemo, useState } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Modal from '@deprecated-ui/common/Modal';
import debounce from 'lodash.debounce';
import If from 'global/ErrorHandlers/If';
import { Skeleton } from 'antd';
import tradingDataStore from 'lib/stores/tradingData';
import {
  TransactionHistoryListItem,
  TransactionHistoryParams,
  TransactionPeriod,
} from 'global/Securities/types/api/history';
import { HistoryTable, SelectContainer, SellBadge } from '../styled';
import { EmptyStateInModal } from '../EmptyState';
import { HistoryModal } from '../HistoryModal';
import {
  middleColumns as middleColumnsStock,
  middleColumnsBonds,
} from '../HistoryTableData';
import { formatDate, renderTableRowContent } from '../utils';
import { shimmerTableData } from '../../ShimmerTable';
import { iModal } from '../../types';
import PeriodSelection from './PeriodSelection';

interface iSymbolHistoryModal extends iModal {
  symbol: string;
  stockType?: string;
}

export const SymbolHistoryModal = ({
  onCancel,
  symbol,
  visible,
  stockType,
}: iSymbolHistoryModal) => {
  const {
    setSkeleton,
    getTransactionHistory,
    transactionHistory: { showSkeleton, data },
  } = tradingDataStore();

  const [currentRecord, setCurrentRecord] = useState(null);
  const [shimmer, showShimmer] = useState(showSkeleton);
  const [period, setPeriod] = useState<TransactionPeriod>(
    TransactionPeriod.OneMonth,
  );

  // Merging the Records
  const displayedData = useMemo(
    () =>
      data?.history
        .map((history) => history.history_list)
        .reduce((prev, curr) => [...prev, ...curr], []),
    [data, symbol, period],
  );

  const handleFilterChanged = useCallback(
    debounce((filters: TransactionHistoryParams) => {
      getTransactionHistory(filters);
    }, 200),
    [],
  );

  const handleHistoryModalCancel = () => {
    setCurrentRecord(null);
  };

  const handlePeriodChanged = (selectedPeriod: TransactionPeriod) => {
    setPeriod(selectedPeriod);
  };

  const getRowClassName = (record) => {
    if (record.groupDate) return 'group-date-row';
    return '';
  };

  const handleRecordClick = (record: TransactionHistoryListItem) => ({
    onClick: () => {
      if (record) {
        setCurrentRecord(record);
      }
    },
  });

  const middleColumns =
    stockType === 'bond' ? middleColumnsBonds : middleColumnsStock;

  const columns = [
    {
      key: 'display_as',
      title: (
        <PeriodSelection
          onChange={handlePeriodChanged}
          period={period}
          popUpId="history-modal-select-container"
        />
      ),
      dataIndex: 'display_as',
      className: 'td-mobile history-order-item',
      width: '13%',
      render: (text: string, dataObject: TransactionHistoryListItem) => (
        <Flex align="center">
          <Text
            color={dataObject.status_color_code}
            as="span"
            family={stockType === 'bond' ? '' : 'bold'}
          >
            {text}
          </Text>
          {dataObject.label && (
            <SellBadge family="bold">
              {dataObject.label.toUpperCase()}
            </SellBadge>
          )}
        </Flex>
      ),
    },
    ...middleColumns,
    {
      key: 'date',
      title: 'Date',
      dataIndex: 'date',
      className: 'td-mobile history-date normal',
      width: '15%',
      render: (text: string, dataObject: TransactionHistoryListItem) =>
        renderTableRowContent(
          text,
          dataObject,
          <Text align="right" family="bold">
            {formatDate(text)}
          </Text>,
        ),
    },
  ];

  const shimmerColumns = [
    {
      key: 'display_as',
      title: (
        <PeriodSelection
          onChange={handlePeriodChanged}
          period={period}
          popUpId="history-modal-select-container"
        />
      ),
      dataIndex: 'display_as',
      className: 'td-mobile history-order-item',
      width: '13%',
      render: () => (
        <Skeleton
          paragraph={false}
          title={{ style: { margin: 'auto' } }}
          loading
          active
        />
      ),
    },
  ];

  useEffect(() => {
    showShimmer(true);

    handleFilterChanged(
      {
        period,
        keyword: symbol,
      },
      false,
    );
  }, [symbol, period]);

  useEffect(() => {
    showShimmer(showSkeleton);
  }, [showSkeleton]);

  useEffect(
    () => () => {
      setSkeleton('transactionHistory', true);
    },
    [],
  );

  return (
    <>
      <HistoryModal
        visible={Boolean(currentRecord)}
        onCancel={handleHistoryModalCancel}
        onCompanyClick={handleHistoryModalCancel}
        record={currentRecord}
      />

      <If condition={!currentRecord}>
        <Modal visible={visible} onCancel={onCancel} centered width={1000}>
          <div style={{ padding: 16 }}>
            <Text align="center" family="bold" size="18px">
              Riwayat Transaksi
            </Text>
            <SelectContainer id="history-modal-select-container" />
            <HistoryTable
              style={{ height: 400 }}
              scroll={{ y: 340 }}
              dataSource={shimmer ? shimmerTableData : displayedData}
              columns={shimmer ? shimmerColumns : columns}
              pagination={false}
              locale={{
                emptyText: <EmptyStateInModal />,
              }}
              rowClassName={getRowClassName}
              onRow={handleRecordClick}
            />
          </div>
        </Modal>
      </If>
    </>
  );
};

export default SymbolHistoryModal;
